import { inject, Injectable, NgZone } from '@angular/core';
import {
  DBStatus,
  DelStatus,
  KundenPrüfungTypen,
  Media,
  MRproResponse,
  MRproResponseInfo,
  SpezResponse,
  StammAuswahl,
  StammDatenResponse,
} from 'src/app/types/api';
import {
  AsAlias,
  AsMappedRows,
  AsMappedTableFE,
  AsTableColumns,
  AsTableFE,
  AsTableOrNull,
  GetDBStatus,
  GetKundenBezFor,
  KNFields1_20,
  MappedColumns,
  SharedDB,
} from './sharedDB';
import { AppDB, KSPEZV, OBAUW, OMESS, ONATUR, OPRUEF, OSPEZ, SXMKP, SYSOOPT } from './dexieDB';
import { MangelDB, OMAZS } from './mangelDB';
import { ZaehlerDB } from './zaehlerDB';
import { BaseItemsDB } from './baseitemsDB';
import { Table } from 'src/app/models/ui/table';
import { Aliased, Entry, Key, Numerated } from 'src/app/types/shared';
import {
  SpezColumnType,
  SpezPointType,
  SpezRowType,
} from 'src/app/content/_components/_overlays/spezifikationsdialog/spezifikationsdialog.component';
import { SaveSpezifikationModel } from 'src/app/types/api';
import { BackendLocaleDatePipe } from 'src/app/pipes/get-locale-date.pipe';
import { MessungÜbersicht } from 'src/app/content/_components/_overlays/zaehlerverwaltung/zaehler.types';
import dayjs from 'dayjs';
import { DetailViewResponse, DetailViewTabs } from 'src/app/content/_components/detailview/detailview.types';
import { SOTYP_Map, SOTYP as SOTYP_Map_Row } from 'src/app/models/api/SOTYP';
import { StammDatenModel } from 'src/app/content/_components/_overlays/stammdatendialog/stammdatendialog.component';
import { FileForPreview } from 'src/app/content/_components/_shared/preview-thumbnails/preview-thumbnail.type';
import {
  BauwerkeModel,
  BauwerkRow,
} from 'src/app/content/_components/_overlays/bauwerksdialog/bauwerksdialog.component';
import {
  BUE_Map,
  BUE_Quadrant,
  QuadrantWert,
} from 'src/app/content/_components/_overlays/bahnuebergangsdialog/bahnuebergangsdialog.component';
import { Position } from 'geojson';
import { SpezRow } from 'src/app/content/_components/_overlays/mangelerfassungsdialog/mangel-erfassung.types';
import { distinct } from 'src/app/misc/functions';
import {
  NaturschutzModel,
  NaturschutzRow,
} from 'src/app/content/_components/_overlays/naturschutzdialog/naturschutzdialog.component';
import {
  GIModel,
  GIRow,
} from 'src/app/content/_components/_overlays/grundinstandsetzungdialog/grundinstandsetzungdialog.component';

//#region Interfaces
export interface SOTYP extends DBStatus, DelStatus {
  ID?: number;
  Name: string;
  Bezeichnung: string;
  Typ: number;
  SysIcon: number;
  Ersteller: string;
  Erstellt: string;
  ParentOTypID: number;
  ParentID: number;
}

export interface OSTAMM extends DBStatus, DelStatus, KundenControlledFelder, KundenAddedLPID, KundenAddedLPDatum {
  ID?: number;
  OSTAMMID: number;
  OTYPID: number;
  Startpunkt: string;
  Endpunkt: string;
  StartMeter: number /* double */;
  EndMeter: number /* double */;
  Bezeichnung: string;
  Laenge?: number /* double */;
  Nutzlaenge?: number /* double */;
  Messdatei?: string;
  Gesperrt?: string;
  Intervall_P?: number;
  Intervall_B?: string /* DateTime */;
  Bemerkung?: string;
  Skizze: string;
  MessRegBezirk?: string;
  Dokumente?: boolean;
  GPS_Lat?: string;
  GPS_Lon?: string;
  LPDatum?: string /* DateTime */;
  LPID?: number;
  SperrID?: number;
  SperrStatus?: number;
  MinFK?: number;
  cTimeStamp?: string /* DateTime */;
  ParentStammID?: number;
  ZTYP: number;
  KAVaktiv?: boolean;
  LFaktiv?: boolean;
  LFmin?: number /* double */;
  LFID?: number;
  MessIdent?: string;
  Intervall_M?: number;
  Intervall_MB?: string /* DateTime */;
  LPMDatum?: string /* DateTime */;
  ZstTyp?: number;
  LNPID?: number;
  LNPDatum?: string /* DateTime */;
  GPS_Lat1?: string;
  GPS_Lon1?: string;
  MessRegID?: number;
  DBStrNr?: string;
}

export interface KundenControlledFelder extends KundenAddedFelder {
  Bezirk?: string;
  Stellwerk?: string;
  Ort?: string;
  Anlagenklasse?: string;
  PlanNr?: string;
  FremdID?: string;
  Eigentuemer?: string;
  Verantwortlich?: string;
  Kostenstelle?: string;
  Messfile?: string;
  Richtung?: string;
  Befahrung?: boolean;
}

export type KPRUVID = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10;
export type KundenAddedLPID = Partial<Numerated<'LPID_P', Exclude<KPRUVID, 1>, number>>;
export type KundenAddedLPDatum = Partial<Numerated<'LPDat_P', Exclude<KPRUVID, 1>, number>>;
export type KundenAddedFelder = Partial<
  Numerated<'KNField', 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | 13 | 14 | 15 | 16 | 17 | 18 | 19 | 20, string>
>;
//#endregion

@Injectable({ providedIn: 'root' })
export class AnlagenDB {
  private readonly zone = inject(NgZone);
  private readonly db = inject(AppDB);

  private readonly mangelDB = inject(MangelDB);
  private readonly zaehlerDB = inject(ZaehlerDB);
  private readonly sharedDB = inject(SharedDB);
  private readonly baseItemsDB = inject(BaseItemsDB);

  async GetSOTYP(): Promise<SOTYP_Map> {
    const objekttypen = await this.db.sotyp
      .where({ ParentID: 0 })
      .and(x => !x.DelStatus)
      .toArray();

    const rows = AsMappedRows<SOTYP_Map_Row>()(objekttypen, [
      ['ID', 'id'],
      ['Bezeichnung', 'bezeichnung'],
      ['SysIcon', 'symbol'],
      ['Typ', 'typ'],
    ]);

    return Object.fromEntries(rows.map(row => [+row.id, row]));
  }

  async GetAnlagen(KPRUVID: number = 1): Promise<Table> {
    return await this.zone.runOutsideAngular(async () => {
      const kbez = await this.sharedDB.GetKundenBezeichnungColumnNames();
      const LPIDField = GetLPIDFieldBy(KPRUVID);
      const LPDatumField = GetLPDatumFieldBy(KPRUVID);

      const objekte = await this.db.ostamm.toArray();
      const withLF = objekte.some(x => x.LFaktiv);
      const withSP = objekte.some(x => x.SperrStatus > 0);

      const cols: MappedColumns<OSTAMM & Aliased<SOTYP, 'sotyp'>> = [
        'OSTAMMID',
        [LPIDField, 'LPID'],
        'OTYPID',
        'MinFK',
        /* Rep nicht nötig in Replikat, es ist immer gesperrt */
        ['sotyp.Bezeichnung', 'typ'],
        ['sotyp.Typ', 'typ_id'],
        ['sotyp.SysIcon', 'typ_icon'],
      ];
      if (withSP) cols.push(['SperrID', 'SPID'], ['SperrStatus', 'SP']);
      if (withLF) cols.push(['LFmin', 'LF']);
      cols.push(
        ['Bezeichnung', 'bezeichnung'],
        ...GetKundenBezFor(['Bezirk', 'Stellwerk', 'Ort'], kbez),
        [LPDatumField, 'Prüfdatum'],
        ['LPMDatum', 'Messdatum'],
        ['Laenge', 'Länge'],
        ['MessRegBezirk', 'MessReg Bezirk'],
        ...GetKundenBezFor(['Messfile'], kbez),
        ['Startpunkt', 'startpunkt'],
        ['Endpunkt', 'endpunkt'],
        ['StartMeter', 'startmeter'],
        ['EndMeter', 'endmeter'],
        ...GetKundenBezFor(['Anlagenklasse', 'PlanNr', 'FremdID'], kbez),
        ['Skizze', 'Messpunktskizze'],
        ...GetKundenBezFor(['Richtung', 'Befahrung', 'Verantwortlich', 'Kostenstelle'], kbez),
        ['Nutzlaenge', 'Leistungslänge'],
        ...GetKundenBezFor(KNFields1_20, kbez),
        ['UserName', 'username'],
        ['Status', 'status']
      );
      const rows = await Promise.all(
        objekte.map(async objekt => {
          const sotyp = await this.db.sotyp.get(objekt.OTYPID);
          if (!sotyp) return null;
          if (!objekt.LFaktiv) objekt.LFmin = null;
          return { ...objekt, ...AsAlias(sotyp, 'sotyp') };
        })
      );

      return AsMappedTableFE()(rows, cols);
    });
  }

  async GetAnlageByID(OSTAMMID: number, KPRUVID: number): Promise<DetailViewResponse> {
    return await this.zone.runOutsideAngular(async () => {
      const tabs: DetailViewTabs = {};
      const result: DetailViewResponse = {
        ostammid: OSTAMMID,
        isRepLocked: false,
        hasStellkraftmessung: false,
        showkav: false,
        tabs,
      };

      const objekt = await this.db.ostamm.where({ OSTAMMID }).first();
      if (!objekt) throw new Error(`Objekt mit OSTAMMID = ${OSTAMMID} nicht gefunden`);

      const otypid = objekt.OTYPID;
      result.otypid = otypid;
      result.bemerkung = objekt.Bemerkung;
      result.bezeichnung = objekt.Bezeichnung;
      result.skizzeName = objekt.Skizze;
      result.skizze = await this.sharedDB.GetMediaByName(objekt.Skizze);

      const sysoopt = (await this.db.sysoopt.where({ OTYPID: otypid }).first()) ?? ({} as SYSOOPT);
      result.objOpt = sysoopt;

      const sotyp = await this.db.sotyp.get(otypid);
      if (!sotyp) throw new Error(`ObjektTyp ${otypid} nicht gefunden`);
      result.typ_Line_Point = sotyp.Typ;
      result.icon = sotyp.SysIcon;

      const LPIDField = GetLPIDFieldBy(KPRUVID);
      const opruef = await this.db.opruef.where({ OPRUEFID: objekt[LPIDField] || -1 }).first();
      const OPRUEFID = opruef?.OPRUEFID;
      result.opruefid = OPRUEFID;

      const zaehler = await this.zaehlerDB.GetZaehlerPruefungen(OSTAMMID);
      if (zaehler) tabs.zaehler = zaehler;
      const stamm = await this.getStammRows(objekt, sotyp, KPRUVID, sysoopt);
      const bildNames = await this.GetPassBilderNames(OSTAMMID, OPRUEFID);
      stamm.push({ Objekt: 'Bild', Bezeichnung: bildNames, DBField: 'opbild.P_Bild' });
      tabs.stamm = AsTableFE(stamm);

      switch (sotyp.Typ) {
        case 1:
        case 3: {
          const spez = await this.getSpezifikationPunkt(OSTAMMID, otypid);
          if (spez) tabs.spez = spez;

          const swtypBez = spez?.find(x => x.orgName?.toLowerCase() == 'strvalue08')?.bezeichnung;
          result.pointtype =
            swtypBez ? ((await this.db.swtyp.where({ Bezeichnung: swtypBez }).first())?.TypID ?? 0) : 0;

          if (!OPRUEFID) break;

          const messungen = await this.getMessungen(OSTAMMID, OPRUEFID);
          if (messungen) tabs.messungen = messungen;

          const maengel = await this.getMangelByOPRUEFID({
            OPRUEFID,
            type: 'punkt',
            condition: omazs => omazs.OMARTID != 2,
          });
          if (maengel) tabs.mangel = maengel;

          const grund = await this.getMangelByOPRUEFID({
            OPRUEFID,
            type: 'punkt',
            condition: omazs => omazs.OMARTID == 2,
          });
          if (grund) tabs.Grundinstandsetzung = grund;
          break;
        }
        default: {
          const spez = await this.getSpezifikationLinie(OSTAMMID, otypid, sotyp.Typ == 4 ? sysoopt : null);
          if (spez) tabs.spez = spez;

          const bauwerke = AsTableOrNull(await this.GetBauwerke(OSTAMMID));
          if (bauwerke) tabs.bauwerke = bauwerke;

          if (!OPRUEFID) break;

          const maengel = await this.getMangelByOPRUEFID({
            OPRUEFID,
            type: 'linie',
            condition: omazs => omazs.OMARTID != 2,
          });
          if (maengel) tabs.mangel = maengel;

          const grund = await this.getMangelByOPRUEFID({
            OPRUEFID,
            type: 'linie',
            condition: omazs => omazs.OMARTID == 2,
          });
          if (grund) tabs.Grundinstandsetzung = grund;
          break;
        }
      }

      return result;
    });
  }

  async GetAnlagenMedia(names: string[]): Promise<MRproResponseInfo<Media>> {
    try {
      const files = await this.sharedDB.GetMediaByNames(names);
      if (!files.length) throw new Error('Auf dem Server sind keine Medien vorhanden');
      return { success: true, files };
    } catch (er) {
      return { success: false, error: (er as Error).message };
    }
  }

  async GetKundenPrüfungenTypen(): Promise<KundenPrüfungTypen> {
    const names = await this.db.kpruv.filter(x => !x.DelStatus).toArray();
    const zuordnungen = await this.db.sxkpo.toArray();

    return {
      kpruv: AsMappedRows<KundenPrüfungTypen['kpruv'][number]>()(names, ['ID', 'Bezeichnung']),
      sxkpo: AsMappedRows<KundenPrüfungTypen['sxkpo'][number]>()(zuordnungen, ['KPRUVID', 'OTYPID']),
    };
  }

  async GetStammDaten(
    OSTAMMID: number,
    OTYPID: number,
    KPRUVID: number,
    OPRUEFID: number,
    istPrüfung: boolean
  ): Promise<MRproResponseInfo<StammDatenResponse>> {
    try {
      const objekt = OSTAMMID == -1 ? ({ OSTAMMID } as OSTAMM) : await this.db.ostamm.where({ OSTAMMID }).first();
      if (!objekt) throw new Error(`Objekt mit OSTAMMID = ${OSTAMMID} nicht gefunden`);

      const sotyp = await this.db.sotyp.get(OTYPID);
      if (!sotyp) throw new Error(`ObjektTyp ${OTYPID} nicht gefunden`);

      const sysoopt = await this.db.sysoopt.where({ OTYPID }).first();
      if (!sysoopt) throw new Error(`Optionstabelle für Typ ${OTYPID} nicht gefunden`);

      const { vonHeute } = await this.mangelDB.IsInspektionVonHeute(OPRUEFID || -1);

      const stamm = await this.getStammRows(objekt, sotyp, KPRUVID, sysoopt, false);
      const bildNames = await this.GetPassBilderNames(OSTAMMID, OPRUEFID);

      return {
        success: true,
        bemerkung: objekt.Bemerkung ?? '',
        list: stamm,
        passbilder:
          !istPrüfung || !sysoopt.Opt_I_Del_Old_Image || vonHeute ? await this.GetPassBilder(bildNames) : null,
        opt_I_PassBild: sysoopt.Opt_I_PassBild,
        opt_F_Zaehler: sysoopt.Opt_F_Zaehler,
        skizzeBase64: (await this.sharedDB.GetMediaByName(objekt.Skizze))?.base64,
        selectedSkizze: objekt.Skizze?.slice(0, objekt.Skizze.lastIndexOf('.')),
        istVomHeute: vonHeute,
        skizzenNames: await this.db.media
          .where({ pfad: 'Skizzen' })
          .toArray(arr => arr.map(x => x.name.slice(0, x.name.lastIndexOf('.')))),
      };
    } catch (ex) {
      return { success: false, error: (ex as Error).message };
    }
  }

  async GetPassBilder(names: string): Promise<FileForPreview[]> {
    try {
      return await this.sharedDB.GetMediaByNames(names?.split('|'));
    } catch (_) {
      return null;
    }
  }

  async GetSkizzeByName(name: string): Promise<string> {
    try {
      name.replace(/\.png/i, '.png');
      const file = await this.sharedDB.GetMediaByName(name);
      return file?.base64;
    } catch (_) {
      return null;
    }
  }

  async SaveSkizzeByName(name: string, base64: string): Promise<MRproResponse> {
    try {
      name = `${name}.png`;
      await this.db.media.put({ pfad: 'Skizzen', name, file: { base64, name } });
      return { success: true };
    } catch (ex) {
      return { success: false, error: (ex as Error).message };
    }
  }

  async SetStammDaten(
    OSTAMMID: number,
    OTYPID: number,
    KPRUVID: number,
    req: StammDatenModel
  ): Promise<MRproResponseInfo<{ mediaStr: string; ostammid: number }>> {
    try {
      const groupIDs = await this.sharedDB.GetGroupIDs();
      const mediaStr = await this.db.transaction(
        'rw',
        [this.db.ostamm, this.db.sxmkp, this.db.sxmgr, this.db.opbild, this.db.media],
        async () => {
          let table = 'ostamm';
          let objekt = { Skizze: `${req.selectedSkizze}.png` } as OSTAMM;
          let sxmkp = {} as SXMKP;
          for (const row of req.data) {
            if (row.isHeader) {
              if (row.Objekt == 'Inspektion') table = 'sxmkp';
              continue;
            }
            switch (table) {
              case 'ostamm': {
                objekt[row.DBField] = row.Bezeichnung;
                break;
              }
              case 'sxmkp': {
                sxmkp[row.DBField] = row.Bezeichnung;
                break;
              }
            }
          }

          if (OSTAMMID == -1) {
            OSTAMMID = await this.createNewObjektAndAssignGruppen(OTYPID, objekt, groupIDs);
            await this.db.sxmkp.add({ ...sxmkp, ZAEID_HP: 0, OSTAMMID, KPRUVID, ...GetDBStatus() });
          } else {
            await this.db.ostamm.where({ OSTAMMID }).modify({
              ...objekt,
              Bemerkung: req.bemerkung,
              ...GetDBStatus(),
            });

            await this.db.sxmkp.where({ OSTAMMID, KPRUVID }).modify({ ...sxmkp, ...GetDBStatus() });
          }
          let mediaStr: string = null;
          if (req.opruefid) {
            mediaStr = await this.sharedDB.SaveMedia(
              'Passbilder',
              req.mediaChange.files,
              req.mediaChange.savedFiles,
              `${OSTAMMID}`
            );

            if (req.mediaChange.deletedFiles?.length) await this.sharedDB.CleanMedia(req.mediaChange.deletedFiles);

            await this.db.opbild.where({ OSTAMMID, OPRUEFID: req.opruefid }).delete();
            if (mediaStr) await this.db.opbild.add({ OSTAMMID, OPRUEFID: req.opruefid, P_Bild: mediaStr });
          }

          return mediaStr;
        }
      );
      return { success: true, ostammid: OSTAMMID, mediaStr };
    } catch (ex) {
      return { success: false, error: (ex as Error).message };
    }
  }

  async SetStamSkizze(OSTAMMID: number, name: string, base64: string): Promise<MRproResponse> {
    try {
      await this.db.transaction('rw', [this.db.ostamm, this.db.media], async () => {
        name = `${name}.png`;
        await this.db.media.put({ pfad: 'Skizzen', name, file: { base64, name } });
        await this.db.ostamm.where({ OSTAMMID }).modify({ Skizze: name, ...GetDBStatus() });
      });
      return { success: true };
    } catch (ex) {
      return { success: false, error: (ex as Error).message };
    }
  }

  async GetSpezifikationen(
    OSTAMMID: number,
    OTYPID: number,
    typ_Line_Point: number
  ): Promise<SpezPointType[] | { columns: SpezColumnType[]; rows: SpezRowType[] }> {
    let sysoopt: SYSOOPT;

    switch (typ_Line_Point) {
      case 1:
      case 3:
        return this.getSpezifikationPunkt(OSTAMMID, OTYPID);
      case 4:
        sysoopt = await this.db.sysoopt.where({ OTYPID }).first();
      case 2:
        return this.getSpezifikationLinie(OSTAMMID, OTYPID, sysoopt);
    }
  }

  async SetSpezifikationen(OSTAMMID: number, req: SaveSpezifikationModel): Promise<MRproResponseInfo<SpezResponse>> {
    try {
      const { OSPEZID, table } = await this.db.transaction(
        'rw',
        [this.db.ospez, this.db.oherst, this.db.kspezv],
        async () => {
          let OSPEZID = req.OSPEZID;
          const table =
            req.smartInsert && (await this.insertSpezifikation(OSTAMMID, req.upwards, req.von, req.bis)) ?
              await this.getSpezifikationLinie(OSTAMMID, req.otypid, null, false)
            : null;

          const spez = Object.fromEntries(
            req.table.flatMap(({ orgName, bezeichnung }) =>
              orgName && orgName != 'OSPEZID' ? [[orgName, bezeichnung]] : []
            )
          ) as OSPEZ;
          if (!OSPEZID) OSPEZID = await this.createNewSpezifikation({ OSTAMMID, ...spez });
          else await this.db.ospez.where({ OSPEZID }).modify({ ...spez, ...GetDBStatus() });

          return { OSPEZID, table };
        }
      );

      return { success: true, OSPEZID, table };
    } catch (ex) {
      return { success: false, error: (ex as Error).message };
    }
  }

  async DeleteSpezifikation(OSPEZID: number): Promise<MRproResponse> {
    try {
      await this.deleteSpezifikationAndHersteller(OSPEZID);
      return { success: true };
    } catch (ex) {
      return { success: false, error: (ex as Error).message };
    }
  }

  async GetSpezLinieTemplates(): Promise<Table> {
    return await this.zone.runOutsideAngular(async () => {
      const zuordnungen = await this.db.sbfek.toArray();
      const rows = await Promise.all(
        zuordnungen.map(async zuordnung => {
          let Bezeichnung = zuordnung.Schienenform_ID;
          const schienenform = Bezeichnung ? await this.db.sschf.get({ Bezeichnung }) : null;

          Bezeichnung = zuordnung.Schwellenart_ID;
          const schwellenart = Bezeichnung ? await this.db.sschw.get({ Bezeichnung }) : null;

          Bezeichnung = zuordnung.Schienenbefestigung_ID;
          const schienenbefestigung = Bezeichnung ? await this.db.sbeft.get({ Bezeichnung }) : null;

          Bezeichnung = zuordnung.Eindeckung_ID;
          const eindeckung = Bezeichnung ? await this.db.seind.get({ Bezeichnung }) : null;

          return {
            ...zuordnung,
            SchienenformID: schienenform?.ID,
            SchwellenartID: schwellenart?.ID,
            SchienenbefestigungID: schienenbefestigung?.ID,
            EindeckungID: eindeckung?.ID,
          };
        })
      );

      return AsMappedTableFE()(rows, [
        'ID',
        'Bezeichnung',
        ['Schienenform_ID', 'Schienenform'],
        ['Schwellenart_ID', 'Schwellenart'],
        ['Schienenbefestigung_ID', 'Schienenbefestigung'],
        ['Eindeckung_ID', 'Eindeckung'],
        'Geschlossen',
        ['BildBase64', 'Bild64'],
        ['UserName', 'Name'],
        'Status',
        'SchienenformID',
        'SchwellenartID',
        'SchienenbefestigungID',
        'EindeckungID',
      ]);
    });
  }

  async GetKSPEZV_Used(): Promise<StammAuswahl[]> {
    const rows = await this.db.kspezv.filter(x => !!x.KFELDNA).sortBy('KFELDNA');
    return AsMappedRows<StammAuswahl>()(rows, ['KFELDNA', 'SFELDNA', 'OTYPID']);
  }

  async GetSelectionFromOSPEZ(OTYPID: number, sqlColName: string): Promise<string[]> {
    return await this.zone.runOutsideAngular(async () => {
      const objekte = await this.db.ostamm.where({ OTYPID }).toArray();
      const rows = await Promise.all(
        objekte.map(async ({ OSTAMMID }) => {
          return await this.db.ospez.where({ OSTAMMID }).toArray();
        })
      );

      return [
        ...new Set(
          rows
            .flat()
            .map(x => x[sqlColName])
            .sort()
        ),
      ];
    });
  }

  async FindObjekteByStammdaten(
    OTYPID: number,
    req: { FilterFeld: string; FilterSelected: string[] }
  ): Promise<number[]> {
    return await this.zone.runOutsideAngular(async () => {
      const spezifikationen = await this.db.ospez.filter(x => req.FilterSelected.includes(x[req.FilterFeld])).toArray();

      const objekte = await Promise.all(
        spezifikationen.map(async ({ OSTAMMID }) => {
          const objekt = await this.db.ostamm.where({ OSTAMMID }).first();
          if (objekt?.OTYPID !== OTYPID) return null;
          return objekt.OSTAMMID;
        })
      );

      return [...new Set(objekte.filter(x => x !== null))];
    });
  }

  async GetBauwerksArten(OTYPID: number): Promise<string[]> {
    const arten = await this.db.sbauw.where({ OTYPID }).sortBy('Bezeichnung');
    return arten.map(x => x.Bezeichnung);
  }

  async GetBauwerke(OSTAMMID: number): Promise<Table<BauwerkRow>> {
    const bauwerke = await this.db.obauw.where({ OSTAMMID }).sortBy('b_StartMeter');
    // bauwerke.sort((a,b) => a.b_StartMeter - b.b_StartMeter);
    return AsMappedTableFE<BauwerkRow>()(bauwerke, [
      ['ID', 'id'],
      ['OSTAMMID', 'ostammid'],
      ['b_ParentID', 'bparentid'],
      ['b_StartMeter', 'von'],
      ['b_EndMeter', 'bis'],
      ['b_Art', 'bauwerksart'],
      ['b_Bezeichnung', 'bezeichnung'],
      ['b_Lage', 'lage'],
      ['b_Zustand', 'zustand'],
      ['b_Bild', 'bild'],
      ['b_GPS', 'gpskoordinaten'],
      ['UserName', 'benutzername'],
      ['Status', 'status'],
    ]);
  }

  async SetBauwerk(OSTAMMID: number, req: BauwerkeModel): Promise<MRproResponseInfo<{ id: number }>> {
    try {
      await this.db.transaction('rw', [this.db.obauw, this.db.media], async () => {
        const mediaStr = await this.sharedDB.SaveMedia(
          'Passbilder',
          req.mediaChange.files,
          req.mediaChange.savedFiles,
          OSTAMMID.toString()
        );
        await this.sharedDB.CleanMedia(req.mediaChange.deletedFiles);
        const item: OBAUW = {
          OSTAMMID,
          b_StartMeter: +req.table.von,
          b_EndMeter: +req.table.bis,
          b_Art: req.table.bauwerksart,
          b_Bezeichnung: req.table.bezeichnung,
          b_Bild: mediaStr,
          b_Lage: req.table.lage,
          b_GPS: req.table.gpskoordinaten,
          ...GetDBStatus(),
        };

        if (req.ID == -1) req.ID = await this.db.obauw.add(item);
        else await this.db.obauw.update(req.ID, { ...item });
      });
      return { success: true, id: req.ID };
    } catch (ex) {
      return { success: false, error: (ex as Error).message };
    }
  }

  async DeleteBauwerk(ID: number): Promise<MRproResponse> {
    try {
      await this.db.obauw.delete(ID);
      return { success: true };
    } catch (ex) {
      return { success: false, error: (ex as Error).message };
    }
  }

  async GetNaturschutzArten(OTYPID: number): Promise<string[]> {
    return await this.zone.runOutsideAngular(async () => {
      const objekten = await this.db.ostamm.where({ OTYPID }).toArray();
      const onatur = await Promise.all(objekten.map(({ OSTAMMID }) => this.db.onatur.where({ OSTAMMID }).toArray()));
      return distinct(onatur.flat(), 'b_Typ');
    });
  }

  async GetNaturschutz(OSTAMMID: number): Promise<Table<NaturschutzRow>> {
    const onatur = await this.db.onatur.where({ OSTAMMID }).sortBy('b_Position');
    return AsMappedTableFE<NaturschutzRow>()(onatur, [
      ['ID', 'id'],
      ['OSTAMMID', 'ostammid'],
      ['b_Position', 'position'],
      ['b_Typ', 'schutzgrad'],
      ['b_Bezeichnung', 'bezeichnung'],
      ['b_Beschreibung', 'beschreibung'],
      ['b_Bild', 'bild'],
      ['UserName', 'benutzername'],
      ['Status', 'status'],
    ]);
  }

  async SetNaturschutz(OSTAMMID: number, req: NaturschutzModel): Promise<MRproResponseInfo<{ id: number }>> {
    try {
      await this.db.transaction('rw', [this.db.onatur, this.db.media], async () => {
        const mediaStr = await this.sharedDB.SaveMedia(
          'Passbilder',
          req.mediaChange.files,
          req.mediaChange.savedFiles,
          OSTAMMID.toString()
        );
        await this.sharedDB.CleanMedia(req.mediaChange.deletedFiles);
        const item: ONATUR = {
          OSTAMMID,
          b_Position: +req.table.position,
          b_Typ: req.table.schutzgrad,
          b_Bezeichnung: req.table.bezeichnung,
          b_Bild: mediaStr,
          b_Beschreibung: req.table.beschreibung,
          ...GetDBStatus(),
        };

        if (req.ID == -1) req.ID = await this.db.onatur.add(item);
        else await this.db.onatur.update(req.ID, { ...item });
      });
      return { success: true, id: req.ID };
    } catch (ex) {
      return { success: false, error: (ex as Error).message };
    }
  }

  async DeleteNaturschutz(ID: number): Promise<MRproResponse> {
    try {
      await this.db.onatur.delete(ID);
      return { success: true };
    } catch (ex) {
      return { success: false, error: (ex as Error).message };
    }
  }

  async GetGrundInstandArten(OTYPID: number): Promise<string[]> {
    /* 
      SELECT DISTINCT sinst.Bezeichnung
      FROM szumz INNER JOIN sinst ON szumz.InstandsetzungID = sinst.ID 
      WHERE OTYPID = ? AND {(otypid == 2 ? "szumz.GattungID = 9" : "sinst.Bezeichnung != ''")}
      ORDER BY Bezeichnung; */

    return await this.zone.runOutsideAngular(async () => {
      const filter: { OTYPID: number; GattungID?: number } = { OTYPID };
      if (OTYPID == 2) filter.GattungID = 9;

      const szumz = await this.db.szumz.where(filter).toArray();
      const sinst = await Promise.all(szumz.map(({ InstandsetzungID: id }) => this.db.sinst.get(id)));
      return distinct(sinst, 'Bezeichnung').filter(r => !!r);
    });
  }

  async GetGrundInstand(OPRUEFID: number, OSTAMMID: number): Promise<Table<GIRow>> {
    /* SELECT OMAZSID as id, {(typ == 2 || typ == 4 ? "StartMeter as von, EndMeter as bis, " : "")}
      Instandsetzung as Grundinstandsetzung,
      UserName, Status
      FROM omazs
      WHERE OPRUEFID = ? AND OMARTID = 2
      ORDER BY OMAZSID; */

    const omazs = OPRUEFID ? await this.db.omazs.where({ OPRUEFID, OSTAMMID, OMARTID: 2 }).sortBy('OMAZSID') : [];

    //* von and bis sind ausgefiltert in component und nur angezeigt, wenn Linie
    return AsMappedTableFE<GIRow>()(omazs, [
      ['OMAZSID', 'id'],
      ['StartMeter', 'von'],
      ['EndMeter', 'bis'],
      ['Instandsetzung', 'Grundinstandsetzung'],
      ['UserName', 'benutzername'],
      ['Status', 'status'],
    ]);
  }

  async SetGrundInstand({ OMAZSID, ids, table }: GIModel): Promise<MRproResponseInfo<{ id: number }>> {
    try {
      const { OSTAMMID, OPRUEFID, OTYPID } = ids;
      await this.db.transaction('rw', [this.db.omazs, this.db.omzlog], async () => {
        OMAZSID ??= await this.mangelDB.createMangel({
          OTYPID,
          OSTAMMID,
          OPRUEFID,
          OMARTID: 2,
          Pruefart: 'Grundinstandsetzung',
          lfNr: 0,
          nIO: true,
        });

        await this.db.omazs.where({ OMAZSID }).modify({
          StartMeter: table.von,
          EndMeter: table.bis,
          Gattung: 'Grundinstandsetzung',
          Stamm: 'Grundinstandsetzung',
          Pruefart: 'Grundinstandsetzung',
          Mangel: 'Grundinstandsetzung',
          FK: 4,
          Betriebsgefahr: false,
          FK_Hand: false,
          ProzentFaktor: 0,
          Faktor: 0,
          Instandsetzung: table.Grundinstandsetzung,
          ...GetDBStatus(),
        });
      });
      return { success: true, id: OMAZSID };
    } catch (ex) {
      return { success: false, error: (ex as Error).message };
    }
  }

  async GetBUE(OPRUEFID: number): Promise<BUE_Map> {
    try {
      const bue = await this.db.obuesd.where({ OPRUEFID }).toArray();
      const bueMap = Object.fromEntries<BUE_Map[keyof BUE_Map]>(
        bue.map(r => [r.Bezeichnung, { Quadrant1_2: r.QUADRANT1_2, Quadrant3_4: r.QUADRANT3_4 }])
      );
      return bueMap;
    } catch (er) {
      console.error('GetBUE: ', (er as Error).message);
      return {};
    }
  }

  async SetBUE(OPRUEFID: number, quadrant: Key<QuadrantWert>, sendObj: BUE_Quadrant): Promise<void> {
    try {
      const colName = quadrant.toUpperCase() as Uppercase<typeof quadrant>;

      await this.db.transaction('rw', [this.db.obuesd], async () => {
        for (const key in sendObj) {
          const value: string = sendObj[key];
          const query = this.db.obuesd.where({ OPRUEFID, Bezeichnung: key });

          if (await query.clone().first()) {
            await query.modify({ [colName]: value, ...GetDBStatus() });
          } else {
            await this.db.obuesd.add({
              OPRUEFID,
              Bezeichnung: key,
              [colName]: value,
              ...GetDBStatus(),
            });
          }
        }
      });
    } catch (er) {
      console.error('SetBUE: ', (er as Error).message);
      throw er;
    }
  }

  async GetBUE_Bilder(OPRUEFID: number): Promise<string[]> {
    return await this.zone.runOutsideAngular(async () => {
      try {
        const namesJoined = (await this.db.opruef.where({ OPRUEFID }).first()).B_BUEBildPfade;
        const names = namesJoined?.split('|');
        if (names?.length != 8) return null;

        if (names.includes('_Q I#')) {
          const temp = [...names];
          names[1] = temp[0];
          names[3] = temp[1];
          names[5] = temp[2];
          names[7] = temp[3];
          names[0] = temp[4];
          names[2] = temp[5];
          names[4] = temp[6];
          names[6] = temp[7];
        }

        const files = await Promise.all(names.map(name => this.sharedDB.GetMediaByName(name)));
        return files.map(file => (file ? `data:image/jpeg;base64,${file.base64}` : ''));
      } catch (er) {
        console.error('GetBUE_Bilder: ', (er as Error).message);
        return [];
      }
    });
  }

  async SetBUE_Bild(OPRUEFID: number, sendObj: { index: number; base64: string }): Promise<MRproResponse> {
    try {
      const { base64, index: i } = sendObj;
      if (!base64) return { success: false, error: 'Empty Bild' };

      await this.db.transaction('rw', [this.db.opruef, this.db.media], async () => {
        const opruef = await this.db.opruef.where({ OPRUEFID }).first();
        const { OSTAMMID, B_BUEBildPfade } = opruef;

        const vst = i % 2 == 1 ? 'MAX' : 'MIN';
        const quadrant =
          i < 2 ? 'I'
          : i < 4 ? 'II'
          : i < 6 ? 'III'
          : 'IV';
        const name = `${OSTAMMID}_${vst}_${quadrant}$${BackendLocaleDatePipe.timestamp}.jpeg`;

        let names = B_BUEBildPfade?.split('|');
        if (names?.length != 8) names = Array(8);
        names[i] = name;
        const namesJoined = names.join('|');

        await this.db.media.put({ pfad: 'Passbilder/Sichtdreieck', name, file: { base64, name } });
        await this.db.opruef.where({ OPRUEFID }).modify({ B_BUEBildPfade: namesJoined, ...GetDBStatus() });
      });

      return { success: true };
    } catch (er) {
      console.error('SetBUE_Bild: ', (er as Error).message);
      return { success: false, error: (er as Error).message };
    }
  }

  async DeleteBUE_Bild(OPRUEFID: number, index: number): Promise<MRproResponse> {
    try {
      await this.db.transaction('rw', [this.db.opruef, this.db.media], async () => {
        let namesJoined = (await this.db.opruef.where({ OPRUEFID }).first()).B_BUEBildPfade;
        if (!namesJoined) return;

        const names = namesJoined?.split('|');
        if (!names[index]) return;

        if (names.length == 8) {
          await this.db.media.delete(names[index]);
          names[index] = '';
          namesJoined = names.join('|');
        } else namesJoined = '';

        await this.db.opruef.where({ OPRUEFID }).modify({ B_BUEBildPfade: namesJoined, ...GetDBStatus() });
      });

      return { success: true };
    } catch (er) {
      console.error('DeleteBUE_Bild: ', (er as Error).message);
      return { success: false, error: (er as Error).message };
    }
  }

  async GetGeo(StammID: number): Promise<Position[]> {
    const coords = (await this.db.kgmko.where({ StammID }).first()).Koordinaten ?? '';

    return coords.split('|').map(pair => {
      const [lat, long] = pair.split(';');
      return [+long, +lat];
    });
  }

  async GetBauartGruppe(OSTAMMID: number): Promise<Record<string, number>> {
    const map: Record<string, number> = {};
    const Name = (await this.db.ospez.where({ OSTAMMID }).first())?.strValue20 ?? '';
    const sbagp = await this.db.sbagp.where({ Name }).toArray();
    for (const { Pruefart, Anzahl } of sbagp) {
      map[Pruefart] = Anzahl;
    }
    return map;
  }

  async GetSpezForStamm(OSTAMMID: number): Promise<SpezRow[]> {
    const ospez = await this.db.ospez.where({ OSTAMMID }).toArray();
    return await Promise.all(
      ospez.map<Promise<SpezRow>>(async row => {
        const schiene = await this.db.sschf.where({ Bezeichnung: row.strValue02 }).first();
        return {
          von: row.s_StartMeter,
          bis: row.s_EndMeter,
          strValue02: schiene?.Typ,
          strValue03: row.strValue03,
          strValue04: row.strValue04,
          strValue05: row.strValue05,
        };
      })
    );
  }

  async GetPassBilderNames(OSTAMMID: number, OPRUEFID: number) {
    if (!OPRUEFID) OPRUEFID = (await this.db.ostamm.where({ OSTAMMID }).first()).LPID || -1;
    const opbild = await this.db.opbild.where({ OSTAMMID, OPRUEFID }).first();
    return opbild?.P_Bild;
  }

  private async getStammRows(
    objekt: OSTAMM,
    sotyp: SOTYP,
    KPRUVID: number,
    sysoopt: SYSOOPT,
    isReadOnly: boolean = true
  ): Promise<StammRow[]> {
    const stammValues: StammRow[] = [];

    const kpruv = await this.db.kpruv.get(KPRUVID);
    const sxmkp = (await this.db.sxmkp.where({ OSTAMMID: objekt.OSTAMMID, KPRUVID }).first()) ?? {
      OSTAMMID: objekt.OSTAMMID,
      IVAL_HP: kpruv?.Base_IVAL_HP || 12,
      IVAL_Beginn_HP: BackendLocaleDatePipe.today,
      IVAL_MS: kpruv?.Base_IVAL_MS || 12,
      IVAL_Beginn_MS: BackendLocaleDatePipe.today,
      KPRUVID,
      ZAEID_HP: 0,
      ...GetDBStatus(),
    };
    if (!sxmkp.ID && objekt.OSTAMMID > 0) sxmkp.ID = await this.db.sxmkp.add(sxmkp);

    if (isReadOnly) {
      stammValues.push({
        DBField: 'sotyp.Bezeichnung',
        Translate: true,
        Objekt: 'Typ',
        Bezeichnung: sotyp.Bezeichnung,
      });
    }

    stammValues.push({
      DBField: 'Bezeichnung',
      Translate: true,
      Fieldtyp: StammInputType.TextInput,
      Pflicht: true,
      Objekt: 'Bezeichnung',
      Bezeichnung: objekt.Bezeichnung,
    });

    const stammKundenFelder = await this.getStammDatenForKundenFelder(objekt);

    const bezirk = stammKundenFelder.get('Bezirk');
    if (bezirk) stammValues.push(bezirk);

    const stellwerk = stammKundenFelder.get('Stellwerk');
    if (stellwerk) stammValues.push(stellwerk);

    const ort = stammKundenFelder.get('Ort');
    if (ort) stammValues.push(ort);

    switch (sotyp.Typ) {
      case 1:
      case 3: {
        if (sysoopt.Opt_O_UseMessReg) {
          stammValues.push({
            DBField: 'MessRegBezirk',
            Translate: true,
            Fieldtyp: StammInputType.TextInput,
            Pflicht: true,
            Objekt: 'MessReg Bezirk',
            Bezeichnung: objekt.MessRegBezirk,
          });

          stammValues.push({
            DBField: 'MessRegID',
            Translate: true,
            Fieldtyp: StammInputType.NumberIntegerInput,
            Pflicht: true,
            Objekt: 'MessRegID',
            Bezeichnung: objekt.MessRegID,
          });
        }

        stammValues.push({
          DBField: 'Laenge',
          Translate: true,
          Fieldtyp: StammInputType.NumberFloatInput,
          Pflicht: true,
          Objekt: 'Baulänge',
          Bezeichnung: objekt.Laenge,
        });

        stammValues.push({
          DBField: 'Nutzlaenge',
          Translate: true,
          Fieldtyp: StammInputType.NumberFloatInput,
          Pflicht: false,
          Objekt: 'Leistungslänge',
          Bezeichnung: objekt.Nutzlaenge,
        });
        break;
      }

      case 2:
      case 4: {
        stammValues.push({
          DBField: 'Startpunkt',
          Translate: true,
          Fieldtyp: StammInputType.DatalistInput,
          Pflicht: true,
          Objekt: 'Startpunkt Bezeichnung',
          Bezeichnung: objekt.Startpunkt,
        });

        stammValues.push({
          DBField: 'Endpunkt',
          Translate: true,
          Fieldtyp: StammInputType.DatalistInput,
          Pflicht: true,
          Objekt: 'Endpunkt Bezeichnung',
          Bezeichnung: objekt.Endpunkt,
        });

        stammValues.push({
          DBField: 'StartMeter',
          Translate: true,
          Fieldtyp: StammInputType.NumberFloatInput,
          Pflicht: true,
          Objekt: 'Startmeter',
          Bezeichnung: objekt.StartMeter,
        });

        stammValues.push({
          DBField: 'EndMeter',
          Translate: true,
          Fieldtyp: StammInputType.NumberFloatInput,
          Pflicht: true,
          Objekt: 'Endmeter',
          Bezeichnung: objekt.EndMeter,
        });

        stammValues.push({
          DBField: 'Laenge',
          Translate: true,
          Fieldtyp: StammInputType.NumberFloatInput,
          Pflicht: true,
          Objekt: 'Gleislänge',
          Bezeichnung: objekt.Laenge,
        });

        stammValues.push({
          DBField: 'Nutzlaenge',
          Translate: true,
          Fieldtyp: StammInputType.NumberFloatInput,
          Pflicht: false,
          Objekt: 'Leistungslänge',
          Bezeichnung: objekt.Nutzlaenge,
        });
        break;
      }
    }

    const anlagenklasse = stammKundenFelder.get('Anlagenklasse');
    if (anlagenklasse) stammValues.push(anlagenklasse);

    const eigentuemer = stammKundenFelder.get('Eigentuemer');
    if (eigentuemer) stammValues.push(eigentuemer);

    const kostenstelle = stammKundenFelder.get('Kostenstelle');
    if (kostenstelle) stammValues.push(kostenstelle);

    const planNr = stammKundenFelder.get('PlanNr');
    if (planNr) stammValues.push(planNr);

    const fremdID = stammKundenFelder.get('FremdID');
    if (fremdID) stammValues.push(fremdID);

    if (sotyp.Typ == 2 || sotyp.Typ == 4) {
      const messfile = stammKundenFelder.get('Messfile');
      if (messfile) stammValues.push(messfile);
    }

    const befahrung = stammKundenFelder.get('Befahrung');
    if (befahrung) stammValues.push({ ...befahrung, Fieldtyp: StammInputType.CheckboxInput });

    const richtung = stammKundenFelder.get('Richtung');
    if (richtung) stammValues.push(richtung);

    const verantwortlich = stammKundenFelder.get('Verantwortlich');
    if (verantwortlich) stammValues.push(verantwortlich);

    if (sotyp.Typ == 1 || sotyp.Typ == 3) {
      stammValues.push({
        DBField: 'GPS_Lat',
        Translate: true,
        Fieldtyp: StammInputType.DatalistInput,
        Objekt: 'GPS Breitengrad',
        Bezeichnung: objekt.GPS_Lat,
      });

      stammValues.push({
        DBField: 'GPS_Lon',
        Translate: true,
        Fieldtyp: StammInputType.DatalistInput,
        Objekt: 'GPS Längengrad',
        Bezeichnung: objekt.GPS_Lon,
      });
    }

    for (const feld of KNFields1_20) {
      const stamm = stammKundenFelder.get(feld);
      if (stamm) stammValues.push(stamm);
    }

    //#region sxmkp
    stammValues.push({
      isHeader: true,
      DBField: 'isHeader',
      Translate: true,
      Objekt: 'Inspektion',
      Bezeichnung: null,
    });

    stammValues.push({
      DBField: 'IVAL_Beginn_HP',
      Translate: true,
      Fieldtyp: StammInputType.DateInput,
      Pflicht: true,
      Objekt: 'Intervallbeginn',
      Bezeichnung: sxmkp.IVAL_Beginn_HP,
    });

    stammValues.push({
      DBField: 'IVAL_HP',
      Translate: true,
      Fieldtyp: StammInputType.NumberIntegerInput,
      Pflicht: true,
      Objekt: 'Prüfintervall',
      Bezeichnung: sxmkp.IVAL_HP,
    });

    if (sysoopt.Opt_F_Zaehler) {
      const Code =
        isReadOnly && sxmkp.ZAEID_HP ? (await this.db.szaehn.get(sxmkp.ZAEID_HP))?.Code : sxmkp.ZAEID_HP || null;

      stammValues.push({
        DBField: 'ZAEID_HP',
        Translate: true,
        Fieldtyp: StammInputType.SelectInput,
        Objekt: 'Zählertyp',
        Bezeichnung: Code,
        Selection:
          isReadOnly ? null : (
            Object.fromEntries(
              (
                await this.db.szaehn
                  .where({ OTYPID: sotyp.ID })
                  .and(x => x.DelStatus == 0)
                  .toArray()
              ).map(x => [x.ID, x.Code])
            )
          ),
      });
    }

    if (!isReadOnly) {
      for (const stamm of stammValues) {
        if (stamm.Fieldtyp == StammInputType.DatalistInput)
          stamm.Selection = await this.getSelectionFromOSTAMM(stamm.DBField);
      }
    }
    return stammValues;
  }

  private async getStammDatenForKundenFelder(objekt: OSTAMM): Promise<Map<Key<KundenControlledFelder>, StammRow>> {
    const kbezng = await this.db.kbezng.filter(x => x.KShow == true).toArray();
    const kundenStammMap = new Map<Key<KundenControlledFelder>, StammRow>();

    for (const { FeldName, KBezeichnung, KPflicht } of kbezng) {
      let DBField = FeldName == 'KundenID' ? 'FremdID' : FeldName;
      kundenStammMap.set(DBField as Key<KundenControlledFelder>, {
        DBField,
        Translate: false,
        Fieldtyp: StammInputType.DatalistInput,
        Pflicht: !!KPflicht,
        Objekt: KBezeichnung,
        Bezeichnung: objekt[DBField],
      });
    }

    return kundenStammMap;
  }
  private async getSelectionFromOSTAMM(colName: string) {
    const rows = await this.db.ostamm.filter(x => !!x[colName]).toArray();
    return [...new Set(rows.map(x => x[colName]).sort())];
  }

  private async getSpezifikationPunkt(
    OSTAMMID: number,
    OTYPID: number,
    OKOMPID: number = -1
  ): Promise<SpezPointType[]> {
    return await this.zone.runOutsideAngular(async () => {
      const fieldnames = await this.db.kspezv
        .where({ OTYPID })
        .and(x => !!x.KFELDNA)
        .sortBy('KSORT');
      if (!fieldnames.length) return null;

      const spezRes =
        OKOMPID == -1 ?
          await this.db.ospez.where({ OSTAMMID }).first()
        : await this.db.ospez
            .where({ OSTAMMID })
            .and(x => x.OKOMPID == OKOMPID)
            .first();

      const rows: SpezPointType[] = await Promise.all(
        fieldnames.map(async (fieldOptions): Promise<SpezPointType> => {
          const sqlColName = fieldOptions.SFELDNA;
          const value = spezRes?.[sqlColName] ?? null;
          const isSelect = fieldOptions.USEWERT;
          const tableName = fieldOptions.TABWERT?.toLowerCase();
          const tab_id = fieldOptions.TABWERT_ID;
          let selection: string[];

          if (sqlColName.startsWith('str')) {
            selection =
              isSelect && tableName ?
                await this.baseItemsDB.GetSelectionFromTable(tableName, tab_id)
              : await this.GetSelectionFromOSPEZ(OTYPID, sqlColName);
            // if (fieldOptions.PFLICHT && !selection.length) selection.push('-');
          }

          return {
            objekt: fieldOptions.KFELDNA,
            bezeichnung: value,
            orgName: sqlColName,
            tabId: tab_id,
            fieldType: sqlColName.slice(0, 3),
            sField: isSelect ? tableName?.toUpperCase() : null,
            wListe: selection,
            pflichtFeld: fieldOptions.PFLICHT,
          };
        })
      );

      return [{ objekt: 'OSPEZID', bezeichnung: spezRes?.OSPEZID ?? -1 }, ...rows];
    });
  }

  private async getSpezifikationLinie(
    OSTAMMID: number,
    OTYPID: number,
    /** @optional if Gleis*/ sysoopt?: SYSOOPT,
    loadColumns: boolean = true
  ): Promise<{ columns: SpezColumnType[]; rows: SpezRowType[] }> {
    return await this.zone.runOutsideAngular(async () => {
      const columns: SpezColumnType[] = [
        {
          id: 'OSPEZID',
          bezeichnung: 'OSPEZID',
        },
        {
          id: 'Startmeter',
          bezeichnung: 's_StartMeter',
          pflichtFeld: 1,
        },
        {
          id: 'Endmeter',
          bezeichnung: 's_EndMeter',
          pflichtFeld: 1,
        },
      ];
      let fieldnames: KSPEZV[] = await this.db.kspezv
        .where({ OTYPID })
        .and(x => !!x.KFELDNA)
        .sortBy('KSORT');
      if (!fieldnames.length) return null;

      if (sysoopt) {
        fieldnames = fieldnames.flatMap(field => {
          const optionName = KSPEZV_SYSOOPT_Map[field.SFELDNA];
          if (field.SFELDNA == 'strValue27') return [];
          if (!optionName) return [field];
          if (sysoopt[optionName]) return [field];
          return [];
        });
      }

      const optionalColumns = await Promise.all(
        fieldnames.map(async (fieldOptions): Promise<SpezColumnType> => {
          const sqlColName = fieldOptions.SFELDNA;
          if (!loadColumns) {
            return { id: fieldOptions.KFELDNA, bezeichnung: sqlColName };
          }
          const isSelect = fieldOptions.USEWERT;
          const tableName = fieldOptions.TABWERT?.toLowerCase();
          const tab_id = fieldOptions.TABWERT_ID;
          let selection: string[];

          if (sqlColName.startsWith('str')) {
            selection =
              isSelect && tableName ?
                await this.baseItemsDB.GetSelectionFromTable(tableName, tab_id)
              : await this.GetSelectionFromOSPEZ(OTYPID, sqlColName);
          }

          return {
            id: fieldOptions.KFELDNA,
            bezeichnung: sqlColName,
            wListe: selection,
            pflichtFeld: fieldOptions.PFLICHT,
            optionalData: {
              fieldType: sqlColName.slice(0, 3),
              tabId: tab_id,
              sField: tableName?.toUpperCase(),
            },
          };
        })
      );
      columns.push(...optionalColumns);

      const spezifikationen = await this.db.ospez.where({ OSTAMMID }).sortBy('s_StartMeter');
      const rows: SpezRowType[] = spezifikationen.map(spezRow => {
        const entries = columns.map(({ id: objekt, bezeichnung: orgName }): Entry<SpezRowType> => {
          return [objekt, { objekt, orgName, value: spezRow[orgName] }];
        });
        return Object.fromEntries(entries);
      });

      return { columns, rows };
    });
  }
  private async insertSpezifikation(OSTAMMID: number, upwards: boolean, von: number, bis: number): Promise<boolean> {
    return await this.db.transaction('rw?', [this.db.ospez, this.db.oherst], async () => {
      let isChanged = false;
      const startNeu = von * (upwards ? 1 : -1);
      const endNeu = bis * (upwards ? 1 : -1);
      const spezifikationen = await this.db.ospez.where({ OSTAMMID }).sortBy('s_StartMeter');

      for (const spez of spezifikationen) {
        const startRow = spez.s_StartMeter * (upwards ? 1 : -1);
        const endRow = spez.s_EndMeter * (upwards ? 1 : -1);

        if (endNeu <= startRow || startNeu >= endRow) continue;
        isChanged = true;

        if (startNeu <= startRow && endNeu >= endRow) {
          await this.deleteSpezifikationAndHersteller(spez.OSPEZID);
        } else if (startNeu > startRow && endNeu < endRow) {
          const newSpez: OSPEZ = { ...spez, ID: undefined, OSPEZID: null, s_StartMeter: bis };
          await this.createNewSpezifikation(newSpez);
          await this.db.ospez.update(spez.ID, { s_EndMeter: von, ...GetDBStatus() });

          break;
        } else if (startNeu <= startRow && endNeu < endRow) {
          await this.db.ospez.update(spez.ID, { s_StartMeter: bis, ...GetDBStatus() });
        } else if (startNeu > startRow && endNeu >= endRow) {
          await this.db.ospez.update(spez.ID, { s_EndMeter: von, ...GetDBStatus() });
        }
      }
      return isChanged;
    });
  }

  private async deleteSpezifikationAndHersteller(OSPEZID: number) {
    await this.db.transaction('rw', [this.db.ospez, this.db.oherst], async () => {
      await this.db.ospez.where({ OSPEZID }).delete();
      await this.db.oherst.where({ OSPEZID }).delete();
    });
  }
  private async createNewSpezifikation(spez: OSPEZ) {
    return await this.db.transaction('rw?', [this.db.ospez], async () => {
      const id = await this.db.ospez.add({ ...spez, ...GetDBStatus() });
      const OSPEZID = 100000000 + id + 50; /* Sicherheit */
      await this.db.ospez.update(id, { OSPEZID });
      return OSPEZID;
    });
  }
  private async getMessungen(OSTAMMID: number, OPRUEFID: number): Promise<Table<MessungÜbersicht>> {
    let messungen = await this.db.omess
      .where({ OPRUEFID })
      .and(x => !x.SZAEHNID && !x.ZTYP)
      .reverse()
      .sortBy('ID');
    let date = (
      await this.db.opruef
        .where({ OPRUEFID })
        .and(x => !x.P_DelStatus)
        .first()
    )?.P_Datum;

    const pruefungen = await this.db.opruef
      .where({ OSTAMMID })
      .and(x => x.OPRUEFID != OPRUEFID)
      .and(x => !x.P_DelStatus)
      .sortBy('P_Datum');

    if (!messungen.length) {
      for (const { OPRUEFID, P_Datum } of pruefungen) {
        messungen = await this.db.omess
          .where({ OPRUEFID })
          .and(x => !x.SZAEHNID && !x.ZTYP)
          .reverse()
          .sortBy('ID');
        if (messungen.length) {
          date = P_Datum;
          break;
        }
      }
    }

    if (!messungen.length) return null;

    interface HistoricMessung {
      P_Datum: OPRUEF['P_Datum'];
      messwerte: {
        Ist: OMESS['Ist'];
        Bezeichnung: OMESS['Bezeichnung'];
        Ausfall: OMESS['Ausfall'];
      }[];
    }

    const historicMeasurements: HistoricMessung[] = await Promise.all(
      pruefungen.map(async ({ OPRUEFID, P_Datum }): Promise<HistoricMessung> => {
        const messungen = await this.db.omess
          .where({ OPRUEFID })
          .and(x => !x.SZAEHNID && !x.ZTYP)
          .reverse()
          .sortBy('ID');
        return { P_Datum, messwerte: messungen };
      })
    );

    let lastInd = 0;
    let lastDatum = dayjs(date).format('DD.MM.YYYY');
    const table = AsMappedTableFE<MessungÜbersicht>()(messungen, [
      'ID',
      'OPRUEFID',
      'Bezeichnung',
      'Soll',
      'SR100minus',
      'SR100plus',
      'SRlimminus',
      'SRlimplus',
      'SRGminus',
      'SRGplus',
      ['Ist', lastDatum],
      ['Ausfall', `Tol-Verl. - ${lastDatum}`],
      /* Insert Index 12 */
      'Instand',
      'Instand_Datum',
      'Instandsetzer',
      'IAusfall',
      'Messender',
      ['INFO', 'Info'],
      ['UserName', 'Username'],
      'Status',
    ]);

    for (let i = 0; i < historicMeasurements.length; i++) {
      const { P_Datum, messwerte } = historicMeasurements[i];
      let Column_Datum = dayjs(P_Datum).format('DD.MM.YYYY');
      if (Column_Datum == lastDatum) {
        Column_Datum = `${Column_Datum} (${i + 1 - lastInd})`;
      } else {
        lastInd = i;
        lastDatum = Column_Datum;
      }

      table.columns.splice(12, 0, ...AsTableColumns([Column_Datum, `Tol-Verl. - ${Column_Datum}`]));

      for (const messung of table.rows) {
        const matched = messwerte.find(wert => wert.Bezeichnung == messung.Bezeichnung);
        if (!matched) continue;
        messung[Column_Datum] = matched.Ist;
        messung[`Tol-Verl. - ${Column_Datum}`] = matched.Ausfall;
      }
    }

    return table;
  }
  private async getMangelByOPRUEFID({
    OPRUEFID,
    type,
    condition,
  }: {
    OPRUEFID: number;
    type: 'linie' | 'punkt';
    condition: (x: OMAZS) => boolean;
  }): Promise<Table> {
    const maengel = await this.db.omazs
      .where({ OPRUEFID })
      .and(x => !!x.Mangel && condition(x))
      .toArray();
    for (const mangel of maengel) {
      if (mangel.FK < 0) mangel.FK = null;
    }

    const cols: MappedColumns<OMAZS> = [
      'OMAZSID',
      ['OSTAMMID', 'ostammid'],
      'lfNr',
      ['Pruefart', 'pruefart'],
      ['Mangel', 'mangel'],
      ['Lage', 'lage'],
      ['Umfang', 'umfang'],
      ['Mengeneinheit', 'mengeneinheit'],
      ['FK', 'fehlerklasse'],
      ['Instandsetzung', 'instandsetzungshinweis'],
      ['Betriebsgefahr', 'betriebsgefahr'],
      ['Bild', 'bildpfad'],
      ['Erledigt', 'erledigtStatus'],
      ['E_Datum', 'erledigt_am'],
      ['E_Firma', 'erledigt_durch'],
      ['InstPic1', 'rueckbilder'],
      ['ProzentFaktor', 'faktor'],
      ['FK_Hand', 'fk_aenderung'],
      ['GattungID', 'gattung_id'],
      ['KAVAAE', 'abnutzungsaequivalent'],
      ['KAVAW', 'anteilige_wichtung'],
      ['EP', 'ep'],
      ['Auftrag', 'auftrag'],
      ['Termin', 'termin'],
      ['AuftragNr', 'auftrag_nr'],
      ['Auftragsart', 'auftragsart'],
      ['Auftragnehmer', 'auftragnehmer'],
      ['SAPExport', 'sap_export'],
      ['MELDSTAT', 'meldstat'],
      ['MELDNR', 'meld_nr'],
      ['AUFSTAT', 'aufstat'],
      ['Faktor', 'faktor1'],
      ['Bemerkung', 'bemerkung'],
      ['UserName', 'username'],
    ];

    if (type == 'linie') cols.splice(2, 0, ['StartMeter', 'von'], ['EndMeter', 'bis']);

    return AsTableOrNull(AsMappedTableFE()(maengel, cols));
  }
  private async createNewObjektAndAssignGruppen(OTYPID: number, objekt: OSTAMM, groupIDs: number[]) {
    return await this.db.transaction('rw?', [this.db.ostamm, this.db.sxmgr], async () => {
      const id = await this.db.ostamm.add({ ...objekt, OTYPID, LPID: null, ...GetDBStatus() });

      const OSTAMMID = +OTYPID.toString().padEnd(7, '0') + id;

      await this.db.ostamm.update(id, { OSTAMMID });
      await this.setObjektInGruppen(OSTAMMID, OTYPID, groupIDs);
      return OSTAMMID;
    });
  }
  private async setObjektInGruppen(AnlagenobjektID: number, Typ: number, Gruppen: number[]) {
    await this.db.sxmgr.bulkAdd(Gruppen.map(BenutzerGruppeID => ({ AnlagenobjektID, BenutzerGruppeID, Typ })));
  }
}

export function GetLPIDFieldBy(kpruvid: number): 'LPID' | keyof KundenAddedLPID {
  CheckKPRUVID(kpruvid);
  return kpruvid == 1 ? 'LPID' : `LPID_P${kpruvid}`;
}

export function GetLPDatumFieldBy(kpruvid: number): 'LPDatum' | keyof KundenAddedLPDatum {
  CheckKPRUVID(kpruvid);
  return kpruvid == 1 ? 'LPDatum' : `LPDat_P${kpruvid}`;
}

export function CheckKPRUVID(id: number): asserts id is KPRUVID {
  if (!Number.isFinite(id) || !Number.isInteger(id) || id < 1 || id > 10) throw new Error('Invalid KPRUVID');
}

export interface StammRow {
  isHeader?: boolean;
  DBField: string;
  Fieldtyp?: StammInputType;
  Pflicht?: boolean;
  Translate?: boolean;
  /** @description Key */
  Objekt: string;
  /** @description Value */
  Bezeichnung: unknown;
  Selection?: Record<number, string> | string[];
}

export const enum StammInputType {
  // ReadonlyInput = -1,
  TextInput,
  DatalistInput,
  NumberFloatInput,
  NumberIntegerInput,
  CheckboxInput,
  DateInput,
  SelectInput,
}

const KSPEZV_SYSOOPT_Map = {
  strValue21: 'Opt_I_Track_Spez_SchwellenNote',
  strValue07: 'Opt_I_Track_Spez_Schwellenjahr',
  strValue22: 'Opt_I_Track_Spez_Schienenhoehe',
  strValue06: 'Opt_I_Track_Spez_Option_Einbaujahr',
  strValue24: 'Opt_I_Track_Spez_Option_ZusatzI',
  strValue25: 'Opt_I_Track_Spez_Option_ZusatzII',
  strValue26: 'Opt_I_Track_Spez_Option_Daemmung',
  strValue23: 'Opt_I_Track_Spez_Option_Unterbau',
  intValue02: 'Opt_I_Track_Spez_Option_GisID',
  intValue01: 'Opt_I_Track_Spez_Option_ZustandsNote',
  strValue08: 'Opt_I_Track_Spez_Schienenjahr',
} as const satisfies Record<string, keyof SYSOOPT>;
